import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { IJourney } from '@/modules/journey/model';
import { formatTimeDuration } from '@/shared/utils/formatTimeDuration';
import { DescriptionList } from '@/shared/view/DescriptionList';
import { Tag } from '@/shared/view/Tag';

import s from './styles.module.css';

const mapToDetails = (journey: IJourney) => ({
  service: () => <Tag>{journey.service}</Tag>,
  duration: journey.duration ? formatTimeDuration(journey.duration) : null,
  chauffeur: () =>
    journey.chauffeur ? (
      <Link className={s.link} to={`/chauffeurs/${journey.chauffeur.id}`}>
        {`${journey.chauffeur.firstName} ${journey.chauffeur.lastName}`}
      </Link>
    ) : null,
  vehicle: () =>
    journey.vehicle ? (
      <Link className={s.link} to={`/vehicles/${journey.vehicle.id}`}>
        <div>
          {journey.vehicle.make} {journey.vehicle.model},
        </div>
        <div>{journey.vehicle.licensePlate}</div>
      </Link>
    ) : null,
});

type TTitleLine = [keyof ReturnType<typeof mapToDetails>, string];

const JourneyDetails = ({ journey }: { journey: IJourney }) => {
  const intl = useIntl();

  const titles = useMemo(
    (): Array<TTitleLine> => [
      [
        'service',
        intl.formatMessage({
          defaultMessage: 'Service',
          id: 'fRbGY0',
          description: 'Journey screen: Service field title',
        }),
      ],
      [
        'duration',
        intl.formatMessage({
          defaultMessage: 'Duration',
          id: 'U07LlE',
          description: 'Journey screen: Duration field title',
        }),
      ],
      [
        'chauffeur',
        intl.formatMessage({
          defaultMessage: 'Chauffeur',
          id: 'zjwb06',
          description: 'Journey screen: Chauffeur field title',
        }),
      ],
      [
        'vehicle',
        intl.formatMessage({
          defaultMessage: 'Vehicle',
          id: 'ZTbur4',
          description: 'Journey screen: Vehicle field title',
        }),
      ],
    ],
    [intl],
  );

  const values = useMemo(() => mapToDetails(journey), [journey]);

  return (
    <DescriptionList
      title={intl.formatMessage({
        defaultMessage: 'Details',
        id: 'WNpCWs',
        description: 'Journey screen: Details section title',
      })}
      titles={titles}
      values={values}
      className={s.details}
    />
  );
};

export { JourneyDetails };
